import type { FC } from "react";

import { Button } from "@/components/ui/button";
import { LanguageMenu } from "@/features/clientPortal/components/ClientPortalShell";
import { PORTAL_LANGUAGES, usePortalT } from "@/features/clientPortal/i18n";

import type { IntakeLanguage } from "./intakeLanguage";

/** Top-right on the client's intake: the language, a switch back to the
 * English questions, and how the translation is doing. */
export const IntakeLanguageBar: FC<{
  state: IntakeLanguage;
  /** The welcome screen also says they may answer in their own language. */
  showHint: boolean;
  fgRgb: string;
  /** Sits under the firm-preview banner instead of behind it. */
  belowBanner?: boolean;
}> = ({ state, showHint, fgRgb, belowBanner = false }) => {
  const { t } = usePortalT();
  const translated = state.language !== "en";
  const name =
    PORTAL_LANGUAGES.find((l) => l.code === state.language)?.name ??
    state.language;
  const muted = { color: `rgba(${fgRgb}, 0.72)` };
  return (
    <div
      className={`absolute right-6 z-20 flex max-w-[min(340px,60vw)] flex-col items-end gap-1.5 mobile:right-4 ${
        belowBanner ? "top-14 mobile:top-20" : "top-5 mobile:top-4"
      }`}
      data-testid="intake-language"
    >
      <div className="flex items-center gap-1.5">
        {translated && state.status === "idle" ? (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 rounded-full px-2.5 text-xs hover:bg-white/10"
            style={muted}
            onClick={() => state.setShowEnglish(!state.showEnglish)}
          >
            {state.showEnglish
              ? t("intake.showTranslated", { language: name })
              : t("intake.showEnglish")}
          </Button>
        ) : null}
        <LanguageMenu
          value={state.language}
          onChange={state.setLanguage}
          className="border-white/30 bg-white/90 text-[#111827] hover:bg-white"
        />
      </div>
      {translated && state.status === "loading" ? (
        <span className="text-xs" style={muted} role="status">
          {t("intake.translating")}
        </span>
      ) : null}
      {translated && state.status === "failed" ? (
        <span className="text-end text-xs" style={muted} role="status">
          {t("intake.translationFailed")}
        </span>
      ) : null}
      {translated && showHint ? (
        <span className="text-end text-xs leading-snug" style={muted}>
          {t("intake.answerHint")}
        </span>
      ) : null}
    </div>
  );
};
